import * as React from 'react';
import { Box } from '@leagueplatform/genesis-core';

interface RemovableChipProps {
  /** Already-localized text shown in the chip. */
  label: string;
  /**
   * Accessible name for the remove action. The whole chip is the button, so
   * this doubles as the chip's accessible name (the visible `×` is decorative).
   */
  removeLabel: string;
  enabled: boolean;
  /** Clears whatever the chip represents. */
  onRemove: () => void;
}

// The single source of truth for the removable-chip look, shared by the answer
// value pills and the data-point pill so they stay pixel-identical.
const chipCss = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '$quarter',
  paddingInline: '$threeQuarters',
  paddingBlock: '$quarter',
  border: 'none',
  borderRadius: '$pill',
  backgroundColor: '$surfaceBackgroundSecondary',
  color: '$onSurfaceTextPrimary',
  fontSize: '$bodyTwo',
  lineHeight: '$bodyTwo',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  '&:disabled': { cursor: 'default' },
} as const;

/**
 * A selected value rendered as a pill whose entire surface is the remove
 * button — `label ×`. When disabled the `×` is dropped and the button is
 * inert, so the pill reads as a static token in read-only mode.
 */
export const RemovableChip: React.FC<RemovableChipProps> = ({
  label,
  removeLabel,
  enabled,
  onRemove,
}) => (
  <Box
    as="button"
    type="button"
    disabled={!enabled}
    onClick={onRemove}
    css={chipCss}
    // When disabled the chip can't be removed and the `×` is hidden, so the
    // accessible name is just the visible label — not a misleading "Remove …".
    aria-label={enabled ? removeLabel : label}
  >
    {label}
    {enabled && <span aria-hidden>×</span>}
  </Box>
);
